import { useId, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { observer } from 'mobx-react-lite';
import { Down } from '@linktivity/link-icons';
import { Select } from '@linktivity/link-ui';
import { PlanPrice } from '@link/share/booking';
import {
  CalendarSkeleton,
  UnitSkeleton
} from '@link/share/business/ProductPage/Skeleton';
import type { CollapsiblePlan } from '@link/share/hooks/productPageSelectors';
import { useBookingStore } from '../context';
import { PlanCalendar } from './PlanCalendar';
import { PlanUnits } from './PlanUnits';
import styles from './planCards.module.css';

interface Props {
  onLoadMore: () => void;
}

/**
 * The standard layout: one expandable card per plan, each with its own
 * calendar, start time and units.
 *
 * The cards — names, starting prices — render on the server from the product.
 * Their insides need the calendar, which the browser loads, so the server and
 * the first browser render show skeletons there.
 */
export const PlanCards = observer(function PlanCards({ onLoadMore }: Props) {
  const store = useBookingStore();
  const { t } = useTranslation();
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        {t('triplabo.product.plans.title')}
      </h2>
      <div className={styles.list}>
        {store.collapsiblePlans.map(plan => (
          <PlanCard
            key={plan.activityPlan.id ?? ''}
            plan={plan}
            onLoadMore={onLoadMore}
          />
        ))}
      </div>
    </section>
  );
});

interface CardProps {
  plan: CollapsiblePlan;
  onLoadMore: () => void;
}

/** One plan: name, starting price and a details button, its fields inside. */
const PlanCard = observer(function PlanCard({ plan, onLoadMore }: CardProps) {
  const store = useBookingStore();
  const { t } = useTranslation();
  const bodyId = useId();
  const planId = plan.activityPlan.id ?? '';
  const expanded = store.activePlanId === planId;
  const price = store.startingPrices.get(planId);

  // Opening a card closes the open one, whose fields unmount at once rather
  // than animate shut. When that card is above this one, everything below it
  // jumps up by its height — a calendar's worth — and this card's header
  // leaves the screen. So note where the header was and scroll it back there
  // before the browser paints.
  const headerRef = useRef<HTMLElement>(null);
  const headerTopBeforeOpen = useRef<number | null>(null);
  useLayoutEffect(() => {
    const before = headerTopBeforeOpen.current;
    headerTopBeforeOpen.current = null;
    const header = headerRef.current;
    if (!expanded || before === null || !header) return;
    const shift = header.getBoundingClientRect().top - before;
    if (shift) window.scrollBy({ top: shift, behavior: 'instant' });
  }, [expanded]);

  const toggle = () => {
    if (!expanded) {
      headerTopBeforeOpen.current =
        headerRef.current?.getBoundingClientRect().top ?? null;
    }
    store.expandPlan(planId, !expanded);
  };
  return (
    <article className={cls(styles.card, expanded && styles.expanded)}>
      <header ref={headerRef} className={styles.header}>
        <h3 className={styles.title}>
          <button
            type="button"
            className={styles.titleButton}
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={toggle}
          >
            {plan.activityPlan.title}
          </button>
        </h3>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.detailsButton}
            onClick={() => store.showPlanDetail(plan.activityPlan)}
          >
            {t('share.details.title')}
          </button>
          {/* A larger hit area for the pointer. Not a button: the title button
              is the toggle for keyboards and assistive tech, and the price in
              here stays readable to them as plain text. */}
          <div className={styles.toggle} onClick={toggle}>
            {price && (
              <PlanPrice
                label={price.label}
                amount={price.amount}
                currency={store.currency}
              />
            )}
            <Down className={styles.chevron} aria-hidden />
          </div>
        </div>
      </header>
      <div id={bodyId} className={styles.body}>
        <div className={styles.bodyClip}>
          <div className={styles.bodyInner}>
            {/* Only the open card's fields are mounted: each would otherwise
                hold a calendar of its own. */}
            {expanded && <PlanFields plan={plan} onLoadMore={onLoadMore} />}
          </div>
        </div>
      </div>
    </article>
  );
});

const PlanFields = observer(function PlanFields({
  plan,
  onLoadMore
}: CardProps) {
  const store = useBookingStore();
  const { t } = useTranslation();
  const planId = plan.activityPlan.id ?? '';
  if (store.calendar.isLoading) {
    return (
      <>
        {plan.showsCalendarDate && <CalendarSkeleton />}
        <UnitSkeleton />
      </>
    );
  }
  const showTimeSlots =
    !plan.hideDateAndTime && plan.timeSlots.some(slot => slot.label.trim());
  return (
    <>
      {plan.showsCalendarDate && (
        <PlanCalendar planId={planId} onLoadMore={onLoadMore} />
      )}
      {showTimeSlots && (
        <div className={styles.timeSlots}>
          <Select
            className={styles.timeSelect}
            options={plan.timeSlots}
            value={plan.selectedTimeSlot}
            onSelect={(timeId: string) => store.selectTime(planId, timeId)}
            placeholder={t('share.common.selectTime')}
          />
        </div>
      )}
      {/* Set apart from the date and time above, as the SPA card did. */}
      <div className={styles.units}>
        <PlanUnits plan={plan} />
      </div>
    </>
  );
});
